import { supabase } from './supabase';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

// Table names for realtime subscriptions
// type TableName = 'orders' | 'order_items' | 'tables' | 'kitchen_orders' | 'reservations' | 'waitlist' | 'notifications';

// Subscribe to order updates for a location
export function subscribeToOrders(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`orders:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `location_id=eq.${locationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to table status changes
export function subscribeToTables(
  floorPlanId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`tables:${floorPlanId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'tables',
        filter: `floor_plan_id=eq.${floorPlanId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to kitchen orders
export function subscribeToKitchenOrders(
  stationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`kitchen:${stationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'kitchen_orders',
        filter: `station_id=eq.${stationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to notifications
export function subscribeToNotifications(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`notifications:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `location_id=eq.${locationId}`,
      },
      callback
    )
    .subscribe();
}

// Broadcast channel for custom events
export function subscribeToBroadcast(
  locationId: string,
  callback: (payload: { event: string; payload: Record<string, unknown> }) => void
): RealtimeChannel {
  return supabase
    .channel(`broadcast:${locationId}`)
    .on('broadcast', { event: '*' }, callback)
    .subscribe();
}

// Send broadcast message
export async function sendBroadcast(
  locationId: string,
  event: string,
  payload: Record<string, unknown>
): Promise<void> {
  const channel = supabase.channel(`broadcast:${locationId}`);
  await channel.send({
    type: 'broadcast',
    event,
    payload,
  });
}

// Subscribe to product updates for a location
// Triggers on any INSERT/UPDATE/DELETE on the products table for the given location
export function subscribeToProducts(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`products:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'products',
        filter: `location_id=eq.${locationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to inventory updates for a location
export function subscribeToInventory(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`inventory:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'inventory_items',
        filter: `location_id=eq.${locationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to inventory transactions (for real-time transaction history)
export function subscribeToInventoryTransactions(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  // Note: inventory_transactions doesn't have location_id directly,
  // so we subscribe to all inserts and filter client-side if needed
  return supabase
    .channel(`inventory-transactions:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'inventory_transactions',
      },
      callback
    )
    .subscribe();
}

// Subscribe to inventory transfers (BOH/FOH movements) for a location
export function subscribeToInventoryTransfers(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`inventory-transfers:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'inventory_transfers',
      },
      callback
    )
    .subscribe();
}

// Subscribe to raw inventory transactions (for real-time raw material movement tracking)
export function subscribeToRawInventoryTransactions(
  organizationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`raw-inventory-transactions:${organizationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'raw_inventory_transactions',
      },
      callback
    )
    .subscribe();
}

// Subscribe to purchase order updates for an organization
export function subscribeToPurchaseOrders(
  organizationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`purchase-orders:${organizationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'purchase_orders',
        filter: `organization_id=eq.${organizationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to goods transfer updates for an organization
export function subscribeToGoodsTransfers(
  organizationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`goods-transfers:${organizationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'goods_transfers',
        filter: `organization_id=eq.${organizationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to inventory count updates for a location
export function subscribeToInventoryCounts(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`inventory-counts:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'inventory_counts',
        filter: `location_id=eq.${locationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to fulfillment updates for an organization
export function subscribeToFulfillments(
  organizationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`fulfillments:${organizationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'fulfillments',
        filter: `organization_id=eq.${organizationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to raw inventory item updates for an organization
export function subscribeToRawInventory(
  organizationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`raw-inventory:${organizationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'raw_inventory_items',
        filter: `organization_id=eq.${organizationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to supplier updates for an organization
export function subscribeToSuppliers(
  organizationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`suppliers:${organizationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'suppliers',
        filter: `organization_id=eq.${organizationId}`,
      },
      callback
    )
    .subscribe();
}

// Subscribe to supplier invoice updates for a location
export function subscribeToSupplierInvoices(
  locationId: string,
  callback: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
): RealtimeChannel {
  return supabase
    .channel(`supplier-invoices:${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'supplier_invoices',
        filter: `location_id=eq.${locationId}`,
      },
      callback
    )
    .subscribe();
}

// Unsubscribe from a channel
export function unsubscribe(channel: RealtimeChannel): void {
  supabase.removeChannel(channel);
}

